import type { Knex } from 'knex'
import { logger } from '../../core/index.js'

/**
 * Get the database client type
 * Returns 'sqlite' as default when client type cannot be determined (e.g., in mocks)
 */
export function getDbClient(db: Knex): 'postgres' | 'mysql' | 'sqlite' {
  const client = (db.client?.config?.client ?? 'sqlite') as string
  if (client === 'pg' || client === 'postgresql') return 'postgres'
  if (client === 'mysql' || client === 'mysql2') return 'mysql'
  return 'sqlite'
}

/**
 * Format timestamp for database insert/update
 * - PostgreSQL: accepts ISO strings directly
 * - MySQL: requires 'YYYY-MM-DD HH:MM:SS' format
 * - SQLite: accepts ISO strings (stored as TEXT)
 *
 * @param db Knex instance to detect driver
 * @param date Date object (defaults to now)
 * @returns Formatted timestamp string
 */
export function formatTimestamp(db: Knex, date: Date = new Date()): string {
  const client = getDbClient(db)

  if (client === 'mysql') {
    // MySQL requires YYYY-MM-DD HH:MM:SS format
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }

  // PostgreSQL and SQLite accept ISO strings
  return date.toISOString()
}

/**
 * Get current timestamp formatted for the database
 */
export function nowTimestamp(db: Knex): string {
  return formatTimestamp(db, new Date())
}

/**
 * Adds timestamp fields to a table during creation.
 * - PostgreSQL: uses timestamptz (timestamp with time zone)
 * - MySQL: uses standard timestamp
 * - SQLite: uses TEXT with datetime('now') for ISO string consistency
 */
export function addTimestamps(table: Knex.CreateTableBuilder, db: Knex) {
  const client = (db.client?.config?.client ?? 'sqlite3') as string
  const isPostgres = client === 'pg' || client === 'postgresql'
  const isSqlite = client === 'better-sqlite3' || client === 'sqlite3' || client === 'sqlite'

  if (isPostgres) {
    // PostgreSQL: usar timestamp with time zone
    table.timestamp('created_at', { useTz: true }).defaultTo(db.fn.now())
    table.timestamp('updated_at', { useTz: true }).defaultTo(db.fn.now())
  } else if (isSqlite) {
    // SQLite: usar datetime('now') que genera strings ISO directamente
    // Nota: SQLite requiere paréntesis extra para funciones en DEFAULT
    table.text('created_at').defaultTo(db.raw("(datetime('now'))"))
    table.text('updated_at').defaultTo(db.raw("(datetime('now'))"))
  } else {
    // MySQL: timestamp estándar
    table.timestamp('created_at').defaultTo(db.fn.now())
    table.timestamp('updated_at').defaultTo(db.fn.now())
  }
}

/**
 * Adds audit fields (created_by, updated_by) to an existing table if missing.
 * Note: SQLite does not support ALTER TABLE ADD COLUMN with a non-constant default.
 */
export async function addAuditFieldsIfMissing(db: Knex, tableName: string): Promise<void> {
  if (!(await db.schema.hasColumn(tableName, 'created_by'))) {
    await db.schema.alterTable(tableName, (table) => {
      // Sin FK para evitar dependencias circulares entre módulos
      table.string('created_by').nullable()
      table.string('updated_by').nullable()
    })
    logger.info(`Added audit fields to: ${tableName}`)
  }
}

/**
 * Adds is_default field to config tables.
 * Allows marking which configuration is the default at runtime.
 */
export async function addConfigDefaultField(db: Knex, tableName: string): Promise<void> {
  if (!(await db.schema.hasColumn(tableName, 'is_default'))) {
    await db.schema.alterTable(tableName, (table) => {
      table.boolean('is_default').defaultTo(false).nullable()
    })
    logger.info(`Added is_default field to config table: ${tableName}`)
  }
}

/**
 * Adds deleted_at field for soft delete support.
 * Called when entity has softDelete: true.
 */
export async function addSoftDeleteFieldIfMissing(db: Knex, tableName: string): Promise<void> {
  if (!(await db.schema.hasColumn(tableName, 'deleted_at'))) {
    await db.schema.alterTable(tableName, (table) => {
      table.datetime('deleted_at').nullable()
    })
    logger.info(`Added soft delete field to: ${tableName}`)
  }
}

/**
 * Adds a column if it does not exist
 */
export async function addColumnIfMissing(
  db: Knex,
  tableName: string,
  columnName: string,
  columnBuilder: (table: Knex.AlterTableBuilder) => void
): Promise<boolean> {
  if (!(await db.schema.hasColumn(tableName, columnName))) {
    await db.schema.alterTable(tableName, columnBuilder)
    logger.info(`Added column: ${tableName}.${columnName}`)
    return true
  }
  return false
}
